import {
  forwardRef,
  type InputHTMLAttributes,
  useEffect,
  useState,
} from 'react';
import { useTheme } from 'styled-components';
import type { WithTestId } from '@/helpers/testIds';
import type { ValidatorMessage } from '@/helpers/validators';
import { Spinner } from '@/styles/common';
import {
  InputStructure,
  type InputStructureProps,
} from '../structures/InputStructure';
import { ScInput, ScInputIcon, ScInputInternalLabel } from './styled';

export type InputBlockProps = InputHTMLAttributes<HTMLInputElement> &
  InputStructureProps &
  WithTestId & {
    icon?: string;
    approved?: boolean;
    internalLabel?: string;
    showInternalLabelWithValue?: boolean;
    pending?: boolean;
    validatorWarning?: (value: string) => void | ValidatorMessage;
  };

export const InputBlock = forwardRef<HTMLInputElement, InputBlockProps>(
  (
    {
      error,
      hideErrorMessage,
      disabled,
      icon,
      internalLabel,
      autoFocus,
      pending,
      testId,
      showInternalLabelWithValue = true,
      ...rest
    },
    ref
  ) => {
    const { mainColor, gray1Color } = useTheme();

    const [inputRightPadding, setInputRightPadding] = useState(0);

    useEffect(() => {
      if (autoFocus && ref) {
        const timerId = setTimeout(() => {
          if (typeof ref !== 'function' && ref.current) {
            ref.current.focus();
          }
        }, 0);

        return () => clearTimeout(timerId);
      }
    }, [autoFocus, ref]);

    return (
      <InputStructure
        disabled={disabled}
        error={error}
        hideErrorMessage={hideErrorMessage}
        setInputRightPadding={setInputRightPadding}
        {...rest}
      >
        {pending && (
          <Spinner background={gray1Color} center color={mainColor} size={20} />
        )}
        {icon && <ScInputIcon alt="" src={icon} />}
        <ScInput
          {...rest}
          autoFocus={autoFocus}
          className={`${rest.value ? 'filled' : ''}`}
          data-testid={testId}
          disabled={disabled}
          error={error != null}
          icon={icon != null}
          inputRightPadding={inputRightPadding}
          ref={ref}
          showInternalLabelWithValue={showInternalLabelWithValue}
          value={pending ? '' : rest.value}
          withInsideLabel={
            rest.withInsideLabel && (showInternalLabelWithValue || !rest.value)
          }
        />

        {internalLabel && (showInternalLabelWithValue || !rest.value) && (
          <ScInputInternalLabel
            htmlFor={rest.id}
            ref={(node: HTMLSpanElement | null) =>
              node && setInputRightPadding(node.clientWidth + 16)
            }
            styleScheme={rest.styleScheme}
            withInsideLabel={rest.withInsideLabel}
          >
            {internalLabel}
          </ScInputInternalLabel>
        )}
      </InputStructure>
    );
  }
);
